/**
 * Normalizes an endpoint value to ensure it's always a string ID
 * @param {string|object} endpoint - The endpoint value to normalize
 * @returns {string} The normalized endpoint ID
 */
export const normalizeFromEndpoint = (endpoint) => {
  if (!endpoint) return '';
  return typeof endpoint === 'object' ? endpoint.id : endpoint;
};

/**
 * Normalizes a channel configuration to ensure from_endpoint is always a string ID
 * @param {object} config - The channel configuration to normalize
 * @returns {object} The normalized channel configuration
 */
export const normalizeChannelConfig = (config) => {
  if (!config) return {};
  return {
    ...config,
    from_endpoint: normalizeFromEndpoint(config.from_endpoint)
  };
};

/**
 * Gets the endpoint value (e.g., phone number, email) from an endpoint ID
 * @param {Array} endpoints - Array of endpoint objects
 * @param {string} id - The endpoint ID to look up
 * @returns {string} The endpoint value
 */
export const getEndpointValueById = (endpoints, id) => {
  if (!id || !endpoints) return '';
  return endpoints.find((ep) => ep.id === id)?.value || '';
};

/**
 * Gets the endpoint label (display name) from an endpoint ID
 * @param {Array} endpoints - Array of endpoint objects
 * @param {string} id - The endpoint ID to look up
 * @returns {string} The endpoint label
 */
export const getEndpointLabelById = (endpoints, id) => {
  if (!id || !endpoints) return '';
  return endpoints.find((ep) => ep.id === id)?.label || '';
};

/**
 * Normalizes a message configuration to ensure all channel configs have normalized from_endpoint
 * @param {object} message - The message configuration to normalize
 * @returns {object} The normalized message configuration
 */
export const normalizeMessageConfig = (message) => {
  if (!message) return {};
  const normalizedMessage = { ...message };
  
  // Normalize each channel config if it exists
  ['email_config', 'sms_config', 'voice_config', 'chat_config'].forEach(channelKey => {
    if (normalizedMessage[channelKey]) {
      normalizedMessage[channelKey] = normalizeChannelConfig(normalizedMessage[channelKey]);
    }
  });

  return normalizedMessage;
};

/**
 * Normalizes a reminder time configuration to ensure all messages have normalized channel configs
 * @param {object} reminderTime - The reminder time configuration to normalize
 * @returns {object} The normalized reminder time configuration
 */
export const normalizeReminderTime = (reminderTime) => {
  if (!reminderTime) return {};
  return {
    ...reminderTime,
    messages: (reminderTime.messages || []).map(normalizeMessageConfig)
  };
};

/**
 * Normalizes a campaign configuration to ensure all reminder times have normalized messages
 * @param {object} campaign - The campaign configuration to normalize
 * @returns {object} The normalized campaign configuration
 */
export const normalizeCampaignConfig = (campaign) => {
  if (!campaign) return {};
  return {
    ...campaign,
    reminder_times: (campaign.reminder_times || []).map(normalizeReminderTime)
  };
}; 